import React, { useState, useEffect } from 'react';
import { 
  Card, CardBody, CardHeader, CardTitle, Table, Input, Button, Row, Col, 
  Badge, Nav, NavItem, NavLink, TabContent, TabPane,
  Form, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import { marketingService } from '../services/api';
import toast from 'react-hot-toast';
import { 
  TrendingUp, Video, Instagram, MessageCircle, Share2, 
  Plus, Calendar, BarChart2, MapPin, Target, DollarSign,
  Brain, Zap, Award, Clock, Users, Megaphone, Info, PieChart as PieChartIcon,
  Settings, ChevronRight
} from 'lucide-react';

const MarketingAnalyticsPage = () => {
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(true);
  const [contentList, setContentList] = useState([]);
  const [correlationData, setCorrelationData] = useState([]);
  const [attributionData, setAttributionData] = useState([]);
  const [strategicAnalysis, setStrategicAnalysis] = useState(null);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('topping');
  
  // Modals
  const [modalContent, setModalContent] = useState(false);
  const [modalMetrics, setModalMetrics] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalGeoAdjust, setModalGeoAdjust] = useState(false);
  const [modalAccounts, setModalAccounts] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [editingAccount, setEditingAccount] = useState({
    brand: 'topping',
    instagram_business_id: '',
    facebook_page_id: '',
    access_token: ''
  });
  
  const [editingContent, setEditingContent] = useState({
    id: null,
    description: '',
    category: ''
  });
  
  // Form States
  const [newContent, setNewContent] = useState({
    platform: 'instagram',
    content_type: 'video',
    title: '',
    url: '',
    posted_at: new Date().toISOString().split('T')[0],
    campaign_name: ''
  });

  const [geoAdjustment, setGeoAdjustment] = useState({
    city_name: '',
    manual_sales_count: 0,
    manual_revenue: 0
  });

  const [newMetrics, setNewMetrics] = useState({
    content_id: '',
    platform: 'instagram',
    metric_date: new Date().toISOString().split('T')[0],
    views: '',
    likes: '',
    shares: '',
    comments: '',
    ad_spend: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contentRes, correlationRes, attributionRes] = await Promise.all([
        marketingService.getContent({ brand: selectedBrand }),
        marketingService.getCorrelation({ days: 30, brand: selectedBrand }),
        marketingService.getAttribution()
      ]);

      if (contentRes.success) setContentList(contentRes.data);
      if (correlationRes.success) setCorrelationData(correlationRes.data);
      if (attributionRes.success) setAttributionData(attributionRes.data);
    } catch (error) {
      console.error('Error fetching marketing data:', error);
      toast.error('Error al cargar datos de marketing');
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategy = async () => {
    try {
      setLoadingStrategy(true);
      const res = await marketingService.getStrategicAnalysis({ brand: selectedBrand });
      console.log('Marketing Strategic Analysis Response:', res);
      
      if (res && res.success && res.data) {
        setStrategicAnalysis(res.data);
        if (res.data.ai_error) {
          toast.error(`IA: ${res.data.ai_error}`, { icon: '🤖' });
        }
      } else {
        console.warn('Malformed strategic analysis response:', res);
        toast.error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error fetching strategic analysis:', error);
      toast.error('Error al cargar análisis estratégico');
    } finally {
      setLoadingStrategy(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await marketingService.syncInstagram({ brand: selectedBrand });
      if (res.success) {
        toast.success(`Datos de Instagram (${selectedBrand}) sincronizados`);
        fetchStrategy();
      }
    } catch (error) {
      console.error('Error syncing instagram:', error);
      toast.error('Error al sincronizar datos');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBrand]);

  useEffect(() => {
    if ((activeTab === '4' || activeTab === '5') && !strategicAnalysis) {
      fetchStrategy();
    }
  }, [activeTab, selectedBrand]);

  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      const res = await marketingService.createContent({ ...newContent, brand: selectedBrand });
      if (res.success) {
        toast.success('Contenido registrado');
        setModalContent(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Error al registrar contenido');
    }
  };

  const handleSaveGeoAdjust = async (e) => {
    e.preventDefault();
    try {
      const res = await marketingService.saveCityAdjustment({ ...geoAdjustment, brand: selectedBrand });
      if (res.success) {
        toast.success('Ajuste geográfico guardado');
        setModalGeoAdjust(false);
        fetchStrategy();
      }
    } catch (error) {
      toast.error('Error al guardar ajuste');
    }
  };

  const handleUpdateMetadata = async (e) => {
    e.preventDefault();
    try {
      const res = await marketingService.updateContentMetadata(editingContent.id, {
        description: editingContent.description,
        category: editingContent.category
      });
      if (res.success) {
        toast.success('Metadatos actualizados');
        setModalEdit(false);
        fetchStrategy();
      }
    } catch (error) {
      toast.error('Error al actualizar metadatos');
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await marketingService.getAccounts();
      if (res.success) setAccounts(res.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    try {
      const res = await marketingService.saveAccount(editingAccount);
      if (res.success) {
        toast.success('Configuración guardada');
        setModalAccounts(false);
        fetchAccounts();
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const openAccountConfig = () => {
    const existing = accounts.find(a => a.brand === selectedBrand);
    setEditingAccount({
      brand: selectedBrand,
      instagram_business_id: existing?.instagram_business_id || '',
      facebook_page_id: existing?.facebook_page_id || '',
      access_token: ''
    });
    setModalAccounts(true);
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSaveMetrics = async (e) => {
    e.preventDefault();
    try {
      const res = await marketingService.saveMetrics({ ...newMetrics, brand: selectedBrand });
      if (res.success) {
        toast.success('Métricas guardadas');
        setModalMetrics(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Error al guardar métricas');
    }
  };

  const openEditModal = (content) => {
    setEditingContent({
      id: content.id,
      description: content.description || '',
      category: content.category || ''
    });
    setModalEdit(true);
  };

  const formatHour = (h) => {
    const ampm = h >= 12 ? 'pm' : 'am';
    const hour12 = h % 12 || 12;
    return `${hour12}:00 ${ampm}`;
  };

  const formatCurrency = (val) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Analytics de Marketing</h2>
          <div className="d-flex align-items-center mt-2">
            <span className="text-muted mr-2">Analizando:</span>
            <Input 
              type="select" 
              style={{ width: '220px', fontWeight: 'bold' }} 
              value={selectedBrand}
              onChange={e => {
                setSelectedBrand(e.target.value);
                setStrategicAnalysis(null);
              }}
              className="border-primary"
            >
              <option value="topping">🍦 Topping Frozen</option>
              <option value="helaup">🍨 Helaup</option>
            </Input>
            <Button size="sm" color="link" onClick={openAccountConfig} className="ml-1 p-0 text-muted">
              <Settings size={18} />
            </Button>
          </div>
        </div>
        <div className="d-flex gap-2">
          <div className="d-flex align-items-center">
            <Button color="info" className="mr-2" onClick={handleSync} disabled={syncing}>
              {syncing ? <div className="spinner-border spinner-border-sm" /> : <Instagram size={16} className="mr-1" />}
              {syncing ? ' Sincronizando...' : ` Sincronizar IG ${selectedBrand}`}
            </Button>
            <Button color="primary" onClick={() => setModalContent(true)}>
              <Plus size={16} className="mr-1" /> Nuevo Video/Post
            </Button>
            <Button color="success" className="ml-2" onClick={() => setModalMetrics(true)}>
              <BarChart2 size={16} className="mr-2" /> Registrar Métricas
            </Button>
          </div>
        </div>
      </div>

      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')}>
            <TrendingUp size={16} className="mr-2" /> Correlación de Ventas
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')}>
            <Video size={16} className="mr-2" /> Rendimiento de Contenido
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={activeTab === '3' ? 'active' : ''} onClick={() => setActiveTab('3')}>
            <Target size={16} className="mr-2" /> Origen de Clientes
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={activeTab === '4' ? 'active' : ''} onClick={() => setActiveTab('4')}>
            <Brain size={16} className="mr-2 text-primary" /> Estrategia IA
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={activeTab === '5' ? 'active' : ''} onClick={() => setActiveTab('5')}>
            <Users size={16} className="mr-2" /> Resumen de Audiencia
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        {/* TAB 1: CORRELATION CHART */}
        <TabPane tabId="1">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h4">Vistas vs Pedidos (Últimos 30 días)</CardTitle>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart data={correlationData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(str) => new Date(str).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                    />
                    <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                    <Tooltip 
                      labelFormatter={(label) => new Date(label).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })}
                      formatter={(value, name) => [
                        name === 'revenue' ? formatCurrency(value) : value, 
                        name === 'views' ? 'Vistas' : (name === 'orders' ? 'Pedidos' : (name === 'revenue' ? 'Ingresos' : name))
                      ]}
                    />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="views" name="views" stroke="#8884d8" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                    <Line yAxisId="right" type="monotone" dataKey="orders" name="orders" stroke="#82ca9d" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 p-3 bg-light rounded">
                <h5><TrendingUp size={16} className="mr-2 text-primary" /> Análisis de Impacto</h5>
                <p className="small text-muted mb-0">
                  Este gráfico permite visualizar si los picos de visualizaciones en redes sociales coinciden con un incremento en el volumen de pedidos. 
                  Úsalo para identificar qué días tus videos "calentaron" a la audiencia antes de comprar.
                </p>
              </div>
            </CardBody>
          </Card>
        </TabPane>

        {/* TAB 2: CONTENT PERFORMANCE */}
        <TabPane tabId="2">
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Auditoría de Contenido Digital</h4>
                <Badge color="primary" className="p-2">Últimos {strategicAnalysis?.recentMedia?.length || 0} posts</Badge>
              </div>
              <Table responsive hover className="align-middle border-light">
                <thead className="bg-light text-muted small text-uppercase">
                  <tr>
                    <th style={{ width: '80px' }}>Vista</th>
                    <th>Detalles del Post</th>
                    <th>Clasificación</th>
                    <th>Plataforma</th>
                    <th className="text-center">Alcance</th>
                    <th className="text-center">Engagement (ER)</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {strategicAnalysis?.recentMedia && Array.isArray(strategicAnalysis.recentMedia) && strategicAnalysis.recentMedia.length > 0 ? (
                    strategicAnalysis.recentMedia.map((content, i) => {
                      const totalEngagement = (content?.likes || 0) + (content?.comments || 0);
                      const er = (content?.reach && content.reach > 0) ? ((totalEngagement / content.reach) * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={i}>
                          <td>
                            {content?.media_url ? (
                              <div className="rounded shadow-sm overflow-hidden border" style={{ width: '60px', height: '60px', backgroundColor: '#f8f9fa' }}>
                                <img 
                                  src={content.media_url} 
                                  alt="thumbnail" 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/60?text=Post';
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="bg-light rounded d-flex align-items-center justify-content-center border" style={{ width: '60px', height: '60px' }}>
                                <Video size={20} className="text-muted" />
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="font-weight-bold text-dark mr-2">
                                {content?.description?.substring(0, 40) || 'Sin descripción'}...
                              </span>
                              {content?.permalink && (
                                <a href={content.permalink} target="_blank" rel="noopener noreferrer" className="text-primary">
                                  <Share2 size={14} />
                                </a>
                              )}
                            </div>
                            <div className="small text-muted mt-1">
                              <Calendar size={12} className="mr-1" /> {content?.posted_at ? new Date(content.posted_at).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' }) : '---'}
                            </div>
                          </td>
                          <td>
                            {content?.category ? (
                              <Badge color="info" outline>{content.category}</Badge>
                            ) : (
                              <span className="text-muted small italic">Sin clasificar</span>
                            )}
                          </td>
                          <td>
                            <Badge color={content?.platform === 'tiktok' ? 'dark' : 'danger'} pill className="px-2 py-1">
                              {content?.platform === 'instagram' ? <Instagram size={10} className="mr-1" /> : <Video size={10} className="mr-1" />}
                              {content?.platform ? content.platform.toUpperCase() : '---'}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <div className="font-weight-bold">{content?.reach?.toLocaleString() || '---'}</div>
                            <div className="small text-muted">Personas alcanzadas</div>
                          </td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center">
                              <div className={`font-weight-bold ${er > 5 ? 'text-success' : 'text-dark'}`}>{er}% ER</div>
                              <div className="d-flex gap-2 mt-1 small text-muted">
                                <span><TrendingUp size={10} className="mr-1" />{content?.likes || 0}</span>
                                <span><MessageCircle size={10} className="mr-1" />{content?.comments || 0}</span>
                              </div>
                            </div>
                          </td>
                          <td className="text-right">
                            <div className="d-flex justify-content-end gap-2">
                              <Button outline color="secondary" size="sm" onClick={() => openEditModal(content)}>
                                Clasificar
                              </Button>
                              <Button outline color="primary" size="sm" onClick={() => {
                                setNewMetrics({ ...newMetrics, content_id: content.id, platform: content.platform });
                                setModalMetrics(true);
                              }}>
                                Refrescar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        No se encontraron publicaciones recientes. Sincroniza Instagram para comenzar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </TabPane>

        {/* TAB 3: ATTRIBUTION */}
        <TabPane tabId="3">
          <Row>
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <CardTitle tag="h4">Distribución por Origen</CardTitle>
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart data={attributionData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="lead_source" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="customer_count" name="Clientes" fill="#8884d8">
                          {attributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'][index % 5]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <CardTitle tag="h4">Tasa de Conversión por Fuente</CardTitle>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Fuente</th>
                        <th className="text-center">Clientes</th>
                        <th className="text-center">Con Compras</th>
                        <th className="text-center">% Conv.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributionData.map(item => (
                        <tr key={item.lead_source}>
                          <td><strong>{item.lead_source}</strong></td>
                          <td className="text-center">{item.customer_count}</td>
                          <td className="text-center">{item.converted_count}</td>
                          <td className="text-center">
                            <Badge color="info">
                              {((item.converted_count / item.customer_count) * 100).toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">📊 Fidelización: Clientes Nuevos vs Recurrentes</h4>
                    <Badge color="secondary">Métricas de los últimos 30 días</Badge>
                  </div>
                  <Row className="align-items-center">
                    <Col md={4} className="text-center border-right">
                      <div className="py-3">
                        <div className="text-primary h1 font-weight-bold mb-0">
                          {strategicAnalysis?.raw_metrics?.customer_loyalty?.new_customers || 0}
                        </div>
                        <div className="text-muted small text-uppercase font-weight-bold">Clientes Nuevos</div>
                        <div className="text-muted x-small">(Primera compra este mes)</div>
                      </div>
                      <hr className="my-2" />
                      <div className="py-3">
                        <div className="text-success h1 font-weight-bold mb-0">
                          {strategicAnalysis?.raw_metrics?.customer_loyalty?.returning_customers || 0}
                        </div>
                        <div className="text-muted small text-uppercase font-weight-bold">Clientes Fieles</div>
                        <div className="text-muted x-small">(Clientes que regresaron)</div>
                      </div>
                    </Col>
                    <Col md={8}>
                      <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Nuevos', value: strategicAnalysis?.raw_metrics?.customer_loyalty?.new_customers || 0 },
                                { name: 'Recurrentes', value: strategicAnalysis?.raw_metrics?.customer_loyalty?.returning_customers || 0 }
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={70}
                              outerRadius={100}
                              paddingAngle={5}
                              dataKey="value"
                              label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              <Cell fill="#007bff" />
                              <Cell fill="#28a745" />
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={36}/>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </Col>
                  </Row>
                  <div className="mt-4 p-3 bg-light rounded text-center small text-muted">
                    <Info size={14} className="mr-1" />
                    Esta métrica te ayuda a entender si tu marketing está <strong>atrayendo gente nueva</strong> o <strong>manteniendo activa a tu base actual</strong>.
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* TAB 4: AI STRATEGY */}
        <TabPane tabId="4">
          {loadingStrategy ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status" />
              <p className="text-muted">El Súper Agente está analizando tus datos...</p>
            </div>
          ) : strategicAnalysis?.ai_available ? (
            <div className="animate__animated animate__fadeIn">
              <Row>
                <Col md={12}>
                  <Card className="shadow-sm border-0 mb-4 bg-primary text-white">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <Brain size={32} className="mr-3" />
                        <h3 className="mb-0">Análisis Estratégico de IA</h3>
                      </div>
                      <p className="lead mb-0">{strategicAnalysis.summary}</p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Card className="h-100 shadow-sm border-0">
                    <CardBody>
                      <div className="d-flex align-items-center mb-3 text-info">
                        <Zap size={20} className="mr-2" />
                        <h5 className="mb-0">Plan de Contenido</h5>
                      </div>
                      <h3 className="text-dark mb-1">{strategicAnalysis?.content_plan?.weekly_frequency}</h3>
                      <p className="text-muted small mb-3">Frecuencia recomendada semanal</p>
                      
                      <h6>Temas Ganadores:</h6>
                      <ul className="pl-3">
                        {strategicAnalysis?.content_plan?.top_topics?.map((t, i) => (
                          <li key={i} className="mb-1 text-muted small">{t}</li>
                        ))}
                      </ul>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100 shadow-sm border-0">
                    <CardBody>
                      <div className="d-flex align-items-center mb-3 text-success">
                        <Clock size={20} className="mr-2" />
                        <h5 className="mb-0">Horas Doradas</h5>
                      </div>
                      <div className="d-flex align-items-center mb-1">
                        <Clock size={16} className="mr-1" />
                        <h6 className="mb-0">Horas Recomendadas:</h6>
                        <Badge color="warning" className="ml-auto">Hora Colombia</Badge>
                      </div>
                      <div className="mb-3">
                        {strategicAnalysis?.content_plan?.best_hours?.map((h, i) => (
                          <Badge key={i} color="success" className="mr-2 mb-2 p-2" style={{ fontSize: '0.9rem' }}>{h}</Badge>
                        ))}
                      </div>
                      <p className="text-muted small">Momentos de mayor conexión basados en tu audiencia.</p>

                      <h6 className="mt-3">Hashtags Recomendados:</h6>
                      <div className="d-flex flex-wrap gap-1">
                        {strategicAnalysis?.content_plan?.recommended_hashtags?.map((h, i) => (
                          <Badge key={i} color="light" className="text-primary border mb-1 mr-1">{h}</Badge>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100 shadow-sm border-0 bg-info text-white">
                    <CardBody>
                      <div className="d-flex align-items-center mb-3 text-white">
                        <Megaphone size={20} className="mr-2" />
                        <h5 className="mb-0">Estrategia de Pauta</h5>
                      </div>
                      <p className="font-weight-bold mb-2">{strategicAnalysis?.ads_strategy?.recommended_budget_allocation}</p>
                      
                      {strategicAnalysis?.boost_candidates?.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-white small uppercase">Candidatos a Boost:</h6>
                          {strategicAnalysis.boost_candidates.map((b, i) => {
                            // Extract ID if the AI prepended it (e.g., "ID 1115 - Title")
                            const aiTitle = b.title || '';
                            const idMatch = aiTitle.match(/ID\s*(\d+)/i);
                            const extractedId = idMatch ? idMatch[1] : null;

                            // Try to find the actual post in recentMedia by extracted ID, Title, or searching in Description
                            const post = strategicAnalysis.recentMedia?.find(m => 
                              (extractedId && String(m.id) === String(extractedId)) ||
                              String(m.id) === String(aiTitle) || 
                              m.title === aiTitle ||
                              (m.description && aiTitle && m.description.toLowerCase().includes(aiTitle.toLowerCase().replace(/ID\s*\d+\s*-\s*/i, '')))
                            );
                            return (
                              <div key={i} className="p-2 mb-2 rounded bg-white-20 small d-flex align-items-start">
                                {post?.media_url ? (
                                  <img 
                                    src={post.media_url} 
                                    alt="thumb" 
                                    style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px' }} 
                                    className="mr-2 border border-light"
                                  />
                                ) : (
                                  <div className="mr-2 bg-white-10 rounded d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                    <Video size={14} />
                                  </div>
                                )}
                                <div className="flex-grow-1">
                                  <div className="font-weight-bold mb-1">
                                    {post?.title ? (post.title.substring(0, 40) + '...') : b.title}
                                  </div>
                                  <div className="opacity-80 mb-1">{b.reason}</div>
                                  {post?.permalink && (
                                    <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="text-white font-weight-bold d-flex align-items-center" style={{ textDecoration: 'underline' }}>
                                      <Instagram size={10} className="mr-1" /> Ver Video Original
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {strategicAnalysis?.content_grid?.length > 0 && (
                <Row className="mt-4">
                  <Col md={12}>
                    <Card className="shadow-sm border-0">
                      <CardHeader className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">📅 Parrilla Semanal Programada (Sugerida)</h5>
                        <Badge color="primary" pill>Propuesta Estratégica</Badge>
                      </CardHeader>
                      <CardBody>
                        <Table responsive hover className="align-middle mb-0">
                          <thead className="bg-light small text-uppercase">
                            <tr>
                              <th>Día</th>
                              <th>Tipo</th>
                              <th>Tema Sugerido</th>
                              <th>Objetivo Principal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {strategicAnalysis.content_grid.map((item, i) => (
                              <tr key={i}>
                                <td className="font-weight-bold">{item.day}</td>
                                <td>
                                  <Badge color={item.type === 'Reel' ? 'danger' : 'info'} outline>
                                    {item.type}
                                  </Badge>
                                </td>
                                <td>{item.topic}</td>
                                <td><span className="text-muted small">{item.goal}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              <Row className="mt-4">
                <Col md={6}>
                  <Card className="shadow-sm border-0 bg-dark text-white">
                    <CardBody>
                      <div className="d-flex align-items-center mb-2">
                        <BarChart2 size={20} className="mr-2 text-warning" />
                        <h5 className="mb-0">Métricas de Impacto</h5>
                      </div>
                      <Row className="text-center mt-3">
                        <Col xs={6}>
                          <h2 className="mb-0 text-warning">{strategicAnalysis?.kpis?.growth_potential}</h2>
                          <p className="small text-muted">Potencial de Crecimiento</p>
                        </Col>
                        <Col xs={6}>
                          <p className="small mb-0 text-muted">Correlación</p>
                          <small className="text-white-50">{strategicAnalysis?.kpis?.sales_vs_views_correlation}</small>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="shadow-sm border-0 h-100 bg-info text-white">
                    <CardBody className="d-flex flex-column justify-content-center">
                      <div className="d-flex align-items-center mb-2">
                        <Award size={20} className="mr-2" />
                        <h5 className="mb-0">Consejo Maestro</h5>
                      </div>
                      <p className="mb-0 italic">"{strategicAnalysis?.strategic_advice}"</p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center py-5">
              <Brain size={48} className="text-muted mb-3" />
              {strategicAnalysis?.ai_error ? (
                <>
                  <p className="text-danger"><strong>🤖 {strategicAnalysis.ai_error}</strong></p>
                  <p className="text-muted">Los datos crudos están disponibles en la pestaña <strong>Resumen de Audiencia</strong>.</p>
                </>
              ) : (
                <p>No se pudo generar el análisis. Asegúrate de tener datos de Instagram sincronizados.</p>
              )}
              <Button color="primary" onClick={fetchStrategy}>Reintentar Análisis</Button>
            </div>
          )}
        </TabPane>

        {/* TAB 5: AUDIENCE SUMMARY */}
        <TabPane tabId="5">
          <Row>
            <Col md={3}>
              <Card className="shadow-sm border-0 bg-light mb-4">
                <CardBody className="text-center">
                  <Users className="text-primary mb-2" size={24} />
                  <h6 className="text-muted small uppercase">Seguidores Totales</h6>
                  <h2 className="mb-0">
                    {strategicAnalysis?.raw_metrics?.followers_count 
                      ? Number(strategicAnalysis.raw_metrics.followers_count).toLocaleString('es-CO') 
                      : '--'}
                  </h2>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm border-0 bg-light mb-4">
                <CardBody className="text-center">
                  <TrendingUp className="text-success mb-2" size={24} />
                  <h6 className="text-muted small uppercase">Alcance (Reach)</h6>
                  <h2 className="mb-0">{strategicAnalysis?.raw_metrics?.reach || '--'}</h2>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm border-0 bg-light mb-4">
                <CardBody className="text-center">
                  <Video className="text-info mb-2" size={24} />
                  <h6 className="text-muted small uppercase">Publicaciones</h6>
                  <h2 className="mb-0">{strategicAnalysis?.raw_metrics?.media_count || '--'}</h2>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm border-0 bg-light mb-4">
                <CardBody className="text-center">
                  <MessageCircle className="text-warning mb-2" size={24} />
                  <h6 className="text-muted small uppercase">Interacciones</h6>
                  <h2 className="mb-0">{strategicAnalysis?.raw_metrics?.engagement || '--'}</h2>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Card className="shadow-sm border-0 h-100">
                <CardHeader className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Residencia de Audiencia (Top Ciudades)</h5>
                  <MapPin size={18} className="text-primary" />
                </CardHeader>
                <CardBody>
                  {strategicAnalysis?.audience_metrics?.city?.length > 0 ? (
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Ciudad</th>
                            <th className="text-right">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {strategicAnalysis?.audience_metrics?.city && Array.isArray(strategicAnalysis.audience_metrics.city) && strategicAnalysis.audience_metrics.city.slice(0, 5).map((city, i) => (
                            <tr key={i}>
                              <td>{city?.name || '---'}</td>
                              <td className="text-right font-weight-bold">{city?.value?.toLocaleString() || '0'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-muted py-4">Sincroniza para ver datos demográficos</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0 h-100">
                <CardHeader className="bg-white border-0 py-3">
                  <h5 className="mb-0">Distribución de Género</h5>
                </CardHeader>
                <CardBody className="d-flex align-items-center justify-content-around">
                  {strategicAnalysis?.audience_metrics?.gender && Array.isArray(strategicAnalysis.audience_metrics.gender) && strategicAnalysis.audience_metrics.gender.length > 0 ? (
                    strategicAnalysis.audience_metrics.gender.map((g, i) => (
                      <div key={i} className="text-center p-3 border rounded bg-light" style={{ minWidth: '100px' }}>
                        <h6 className="text-muted mb-1">
                          {g?.name === 'M' ? 'Hombres' : g?.name === 'F' ? 'Mujeres' : 'Otros'}
                        </h6>
                        <h3 className={`mb-0 ${g?.name === 'M' ? 'text-primary' : g?.name === 'F' ? 'text-danger' : 'text-secondary'}`}>
                          {g?.value}%
                        </h3>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted py-4 w-100">Sin datos de género</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={7}>
              <Card className="shadow-sm border-0 h-100">
                <CardHeader className="bg-white border-0 py-3">
                  <h5 className="mb-0">Distribución de Edad</h5>
                </CardHeader>
                <CardBody>
                  {strategicAnalysis?.audience_metrics?.age?.length > 0 ? (
                    <div style={{ width: '100%', height: 250 }}>
                      <ResponsiveContainer>
                        <BarChart data={strategicAnalysis.audience_metrics.age}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} />
                          <YAxis hide />
                          <Tooltip 
                            contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                            formatter={(value) => [`${value}%`, 'Audiencia']}
                          />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {Array.isArray(strategicAnalysis.audience_metrics.age) && strategicAnalysis.audience_metrics.age.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#3B82F6' : '#6366F1'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center text-muted py-4">No hay datos de edad disponibles</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={5}>
              <Card className="shadow-sm border-0 h-100">
                <CardHeader className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Residencia de Audiencia (Top Países)</h5>
                  <Target size={18} className="text-primary" />
                </CardHeader>
                <CardBody>
                  {strategicAnalysis?.audience_metrics?.country?.length > 0 ? (
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0 align-middle">
                        <thead className="text-muted small text-uppercase">
                          <tr>
                            <th>País</th>
                            <th className="text-right">Seguidores</th>
                          </tr>
                        </thead>
                        <tbody>
                          {strategicAnalysis?.audience_metrics?.country && Array.isArray(strategicAnalysis.audience_metrics.country) && strategicAnalysis.audience_metrics.country.slice(0, 5).map((country, i) => (
                            <tr key={i}>
                              <td className="font-weight-medium">{country?.name || '---'}</td>
                              <td className="text-right font-weight-bold">{country?.value?.toLocaleString() || '0'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-muted py-4">Sincroniza para ver datos por país</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">📍 Análisis Geográfico: Audiencia vs Ventas</h4>
                    <div className="d-flex align-items-center">
                      <Button size="sm" color="warning" outline className="mr-2" onClick={() => setModalGeoAdjust(true)}>
                        <Plus size={14} className="mr-1" /> Ajustar Ventas Manuales
                      </Button>
                      <Badge color="info">Últimos 90 días</Badge>
                    </div>
                  </div>
                  <p className="text-muted small mb-4">
                    Cruzamos tus seguidores de Instagram con tus ventas reales para identificar dónde vale la pena hacer publicidad geolocalizada.
                  </p>
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Ciudad / Región</th>
                          <th className="text-center">Audiencia (IG)</th>
                          <th className="text-center">Cant. Pedidos</th>
                          <th className="text-center">Ventas ($)</th>
                          <th className="text-center">Índice Conv.</th>
                          <th>Estado de Oportunidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {strategicAnalysis?.raw_metrics?.geographic_analysis?.map((item, idx) => {
                          const index = parseFloat(item.index);
                          let status = { label: 'Analizando', color: 'secondary' };
                          
                          if (item.sales_90d === 0 && item.audience > 1000) {
                            status = { label: 'Oportunidad Alta (Sin ventas)', color: 'warning' };
                          } else if (index < 5 && item.audience > 2000) {
                            status = { label: 'Baja penetración', color: 'danger' };
                          } else if (index > 20) {
                            status = { label: 'Hub de Ventas', color: 'success' };
                          } else {
                            status = { label: 'Estable', color: 'info' };
                          }

                          return (
                            <tr key={idx}>
                              <td><strong>{item.city}</strong></td>
                              <td className="text-center">{item.audience.toLocaleString()}</td>
                              <td className="text-center">{item.sales_90d}</td>
                              <td className="text-center">{formatCurrency(item.revenue_90d || 0)}</td>
                              <td className="text-center">
                                <Badge color="light" className="text-dark">
                                  {index > 1000 ? '>1000' : item.index} <small className="text-muted">v/1k seg.</small>
                                </Badge>
                              </td>
                              <td>
                                <Badge color={status.color} pill>{status.label}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  <div className="mt-3 p-3 border-left border-info bg-light small">
                    <strong>💡 Recomendación Pro:</strong> Si una ciudad tiene mucha <strong>Audiencia</strong> pero pocas <strong>Ventas</strong>, es el lugar ideal para lanzar una campaña de "Envío Gratis" o un descuento local para activar ese mercado.
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </TabContent>

      {/* MODAL: NUEVO CONTENIDO */}
      <Modal isOpen={modalContent} toggle={() => setModalContent(false)} size="lg">
        <ModalHeader toggle={() => setModalContent(false)}>Registrar Nuevo Video / Post</ModalHeader>
        <Form onSubmit={handleCreateContent}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Plataforma</Label>
                  <Input type="select" value={newContent.platform} onChange={e => setNewContent({...newContent, platform: e.target.value})}>
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="facebook">Facebook</option>
                    <option value="youtube">YouTube</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Tipo de Contenido</Label>
                  <Input type="select" value={newContent.content_type} onChange={e => setNewContent({...newContent, content_type: e.target.value})}>
                    <option value="video">Video / Reel / TikTok</option>
                    <option value="image">Imagen / Post</option>
                    <option value="carousel">Carrusel</option>
                    <option value="story">Historia</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Título / Hook del Video</Label>
              <Input 
                placeholder="Ej: Como preparamos el helado de brownie..." 
                value={newContent.title} 
                onChange={e => setNewContent({...newContent, title: e.target.value})} 
                required 
              />
            </FormGroup>
            <FormGroup>
              <Label>URL del Video (TikTok/IG Link)</Label>
              <Input 
                placeholder="https://..." 
                value={newContent.url} 
                onChange={e => setNewContent({...newContent, url: e.target.value})} 
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Vistas (Opcional - Manual)</Label>
                  <Input 
                    type="number"
                    placeholder="Para TikTok/FB"
                    value={newContent.manual_views} 
                    onChange={e => setNewContent({...newContent, manual_views: e.target.value})} 
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Likes (Opcional - Manual)</Label>
                  <Input 
                    type="number"
                    placeholder="Para TikTok/FB"
                    value={newContent.manual_likes} 
                    onChange={e => setNewContent({...newContent, manual_likes: e.target.value})} 
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Fecha de Publicación</Label>
                  <Input 
                    type="date" 
                    value={newContent.posted_at} 
                    onChange={e => setNewContent({...newContent, posted_at: e.target.value})} 
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Campaña (Opcional)</Label>
                  <Input 
                    placeholder="Ej: Lanzamiento Mayo" 
                    value={newContent.campaign_name} 
                    onChange={e => setNewContent({...newContent, campaign_name: e.target.value})} 
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Descripción / Notas</Label>
              <Input 
                type="textarea" 
                value={newContent.description} 
                onChange={e => setNewContent({...newContent, description: e.target.value})} 
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalContent(false)}>Cancelar</Button>
            <Button color="primary" type="submit">Guardar Contenido</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* MODAL: REGISTRAR MÉTRICAS */}
      <Modal isOpen={modalMetrics} toggle={() => setModalMetrics(false)}>
        <ModalHeader toggle={() => setModalMetrics(false)}>Registrar Desempeño Diario</ModalHeader>
        <Form onSubmit={handleSaveMetrics}>
          <ModalBody>
            <FormGroup>
              <Label>Contenido Específico (Opcional)</Label>
              <Input type="select" value={newMetrics.content_id} onChange={e => setNewMetrics({...newMetrics, content_id: e.target.value})}>
                <option value="">Métricas Generales de Plataforma</option>
                {contentList.map(c => <option key={c.id} value={c.id}>{c.title} ({c.platform})</option>)}
              </Input>
            </FormGroup>
            {!newMetrics.content_id && (
              <FormGroup>
                <Label>Plataforma</Label>
                <Input type="select" value={newMetrics.platform} onChange={e => setNewMetrics({...newMetrics, platform: e.target.value})}>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                </Input>
              </FormGroup>
            )}
            <FormGroup>
              <Label>Fecha de la Métrica</Label>
              <Input type="date" value={newMetrics.metric_date} onChange={e => setNewMetrics({...newMetrics, metric_date: e.target.value})} required />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Vistas / Alcance</Label>
                  <Input type="number" value={newMetrics.views} onChange={e => setNewMetrics({...newMetrics, views: e.target.value})} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Likes / Corazones</Label>
                  <Input type="number" value={newMetrics.likes} onChange={e => setNewMetrics({...newMetrics, likes: e.target.value})} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Compartidos</Label>
                  <Input type="number" value={newMetrics.shares} onChange={e => setNewMetrics({...newMetrics, shares: e.target.value})} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Inversión (Pauta)</Label>
                  <Input type="number" placeholder="COP" value={newMetrics.ad_spend} onChange={e => setNewMetrics({...newMetrics, ad_spend: e.target.value})} />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalMetrics(false)}>Cancelar</Button>
            <Button color="success" type="submit">Guardar Métricas</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* MODAL: EDITAR METADATOS */}
      <Modal isOpen={modalEdit} toggle={() => setModalEdit(false)} size="md">
        <ModalHeader toggle={() => setModalEdit(false)}>
          <Award size={20} className="mr-2 text-primary" /> Clasificar Contenido
        </ModalHeader>
        <Form onSubmit={handleUpdateMetadata}>
          <ModalBody>
            <FormGroup>
              <Label>Categoría / Clasificación</Label>
              <Input 
                type="select" 
                value={editingContent.category}
                onChange={(e) => setEditingContent({ ...editingContent, category: e.target.value })}
              >
                <option value="">Seleccionar categoría...</option>
                <option value="Receta">Receta / Preparación</option>
                <option value="Educativo">Educativo / Tutorial</option>
                <option value="Informativo">Informativo / Producto</option>
                <option value="Humor/POV">Humor / POV / Tendencia</option>
                <option value="Promoción">Promoción / Descuento</option>
                <option value="Lifestyle">Estilo de Vida / Marca</option>
                <option value="Testimonio">Testimonio / Cliente</option>
                <option value="Emprendimiento">Consejo Emprendedor</option>
                <option value="Producto Directo">Venta Directa de Producto</option>
              </Input>
              <small className="text-muted">Ayuda a la IA a entender de qué trata este video.</small>
            </FormGroup>
            <FormGroup>
              <Label>Descripción Manual / Objetivos</Label>
              <Input 
                type="textarea" 
                rows="4"
                value={editingContent.description}
                onChange={(e) => setEditingContent({ ...editingContent, description: e.target.value })}
                placeholder="Escribe detalles adicionales o el objetivo de este post..."
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setModalEdit(false)}>Cancelar</Button>
            <Button color="primary" type="submit">Guardar Cambios</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* MODAL: AJUSTE GEOGRÁFICO MANUAL */}
      <Modal isOpen={modalGeoAdjust} toggle={() => setModalGeoAdjust(false)} size="md">
        <ModalHeader toggle={() => setModalGeoAdjust(false)}>Ajuste Manual de Ventas por Ciudad</ModalHeader>
        <Form onSubmit={handleSaveGeoAdjust}>
          <ModalBody>
            <p className="small text-muted">
              Usa esto para registrar ventas que no pasan por el sistema principal (ej: ventas directas en sedes como Floridablanca).
            </p>
            <FormGroup>
              <Label>Ciudad (Seleccionar de las detectadas)</Label>
              <Input 
                type="select" 
                value={geoAdjustment.city_name} 
                onChange={e => setGeoAdjustment({...geoAdjustment, city_name: e.target.value})}
                required
              >
                <option value="">Selecciona una ciudad...</option>
                {strategicAnalysis?.raw_metrics?.geographic_analysis?.map((item, idx) => (
                  <option key={idx} value={item.city}>{item.city}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Cantidad de Pedidos (Estimada últimos 90 días)</Label>
              <Input 
                type="number" 
                placeholder="Ej: 50"
                value={geoAdjustment.manual_sales_count} 
                onChange={e => setGeoAdjustment({...geoAdjustment, manual_sales_count: parseInt(e.target.value) || 0})} 
                required
              />
              <small className="text-muted">Número de veces que te compraron.</small>
            </FormGroup>
            <FormGroup>
              <Label>Volumen de Ventas en Pesos ($ COP)</Label>
              <Input 
                type="number" 
                placeholder="Ej: 120000000"
                value={geoAdjustment.manual_revenue} 
                onChange={e => setGeoAdjustment({...geoAdjustment, manual_revenue: parseFloat(e.target.value) || 0})} 
              />
              <small className="text-muted">El monto total de dinero vendido.</small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalGeoAdjust(false)}>Cancelar</Button>
            <Button color="primary" type="submit">Guardar Ajuste</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* MODAL: CONFIGURACIÓN DE CUENTAS */}
      <Modal isOpen={modalAccounts} toggle={() => setModalAccounts(false)} size="md">
        <ModalHeader toggle={() => setModalAccounts(false)}>Configuración de Cuenta: {editingAccount.brand}</ModalHeader>
        <Form onSubmit={handleSaveAccount}>
          <ModalBody>
            <FormGroup>
              <Label>Instagram Business ID</Label>
              <Input 
                value={editingAccount.instagram_business_id} 
                onChange={e => setEditingAccount({...editingAccount, instagram_business_id: e.target.value})} 
                placeholder="Obtenido de Facebook Developers"
              />
            </FormGroup>
            <FormGroup>
              <Label>Facebook Page ID</Label>
              <Input 
                value={editingAccount.facebook_page_id} 
                onChange={e => setEditingAccount({...editingAccount, facebook_page_id: e.target.value})} 
                placeholder="ID de la página de Facebook"
              />
            </FormGroup>
            <FormGroup>
              <Label>Access Token (Larga Duración)</Label>
              <Input 
                type="password"
                value={editingAccount.access_token} 
                onChange={e => setEditingAccount({...editingAccount, access_token: e.target.value})} 
                placeholder="Deja en blanco para mantener el actual"
              />
              <small className="text-muted">Token de la API de Graph (v20.0+)</small>
            </FormGroup>
            <div className="p-3 bg-light rounded small">
              <strong>Nota:</strong> Para conectar una marca nueva, asegúrate de que el token tenga permisos de <code>instagram_basic</code>, <code>instagram_manage_insights</code> y <code>pages_show_list</code>.
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalAccounts(false)}>Cancelar</Button>
            <Button color="primary" type="submit">Guardar Configuración</Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default MarketingAnalyticsPage;
